import { AxiosInstance } from 'axios';
import { ServiceBaseConfig } from './Interfaces/IAccountService';
import { formatDateToYYYYMMDD } from '../utils/date';
import { toShareholderPersonalMetaPayload } from '../utils/shareholderPersonalDocs';

export type RegisterShareholdersResponse = {
  data: {
    id: number;
    slug: string;
    name: string;
    email: string;
    gender: string;
    date_of_birth: string;
    nationality: string;
    share_issuance_date: string | null;
    class_of_share: string | null;
    occupation: string;
    series_of_share: string | null;
    shares_value: string | null;
    privilegies_or_restrictions: string | null;
    conditions: string | null;
    company_id: number;
    is_director: boolean;
    created_at: string | Date;
    updated_at: string | Date;
  };
};

export type registerDocumentsShareholders = {
  document1: string | File | null;
  passport: string | File | null;
  prove_address: string | File | null;
};

export class ShareholdersRegisteringService {
  public static async registerShareholders(
    api: AxiosInstance,
    payload: any,
    config: ServiceBaseConfig
  ): Promise<RegisterShareholdersResponse> {
    const ddi = payload.ddi;
    const ddd = payload.phone.match(/\(\d+\)/g)?.[0].replace(/\D/g, '');
    const telephone = payload.phone
      .replace(ddd || '', '')
      .replace(/\D/g, '')
      .trim();

    const parsedPayload = {
      name: payload?.name,
      gender: payload?.gender,
      date_of_birth: payload?.dateOfBirth,
      ddi,
      ddd,
      number: telephone,
      email: payload?.email,
      nationality: payload?.nationality,
      passport_number: payload?.passportNo,
      passport_expiry_date: payload?.expiryDate,
      passport_issuing_country: payload?.passportIssueCountry,
      passport_issuing_date: payload?.issueDate,
      address: payload?.address1,
      complement: payload?.address2,
      postal_code: payload?.postalCode,
      city: payload?.city,
      state: payload?.state,
      neighborhood: payload?.neighborhood,
      country: payload?.country,
      passport: payload?.passport,
      local_document: payload?.localDocument,
      prove_address: payload?.proveAddress,
      share_issuance_date: payload?.shareIssuanceDate,
      class_of_share: payload?.classOfShares,
      number_of_shares: payload?.numberOfShares,
      privilegies_or_restrictions: payload?.privilegesOrRestrictions,
      conditions: payload?.conditions,
      company_slug: payload?.company_slug,
      document1: payload?.document1,
      is_director: payload?.is_director,
      is_underage: payload?.is_underage,
      occupation: payload?.occupation,
      document_number: payload?.document_number,
      ...toShareholderPersonalMetaPayload(payload)
    };

    const { data } = await api.post('/shareholders', parsedPayload, config);

    return data;
  }

  public static async updateShareholders(
    api: AxiosInstance,
    shareholderId: number,
    payload: any,
    config: ServiceBaseConfig
  ): Promise<RegisterShareholdersResponse> {
    const ddi = payload.ddi;
    const ddd = payload.phone.match(/\(\d+\)/g)?.[0].replace(/\D/g, '');
    const telephone = payload.phone
      .replace(ddd || '', '')
      .replace(/\D/g, '')
      .trim();

    const transformedDate = (dateFormated: string) => {
      const newDate = new Date(dateFormated);
      return formatDateToYYYYMMDD(newDate);
    };

    const parsedPayload = {
      name: payload?.name,
      gender: payload?.gender,
      date_of_birth: payload?.dateOfBirth,
      ddi,
      ddd,
      number: telephone,
      email: payload?.email,
      nationality: payload?.nationality,
      passport_number: payload?.passportNo,
      passport_expiry_date: payload?.expiryDate,
      passport_issuing_country: payload?.passportIssueCountry,
      passport_issuing_date: transformedDate(payload?.issueDate),
      address: payload?.address1,
      complement: payload?.address2,
      postal_code: payload?.postalCode,
      city: payload?.city,
      state: payload?.state,
      neighborhood: payload?.neighborhood,
      country: payload?.country,
      local_document: payload?.localDocument,
      share_issuance_date: payload?.shareIssuanceDate,
      number_of_shares: payload?.numberOfShares,
      company_slug: payload?.company_slug,
      is_director: payload?.is_director ?? 0,
      is_underage: payload?.is_underage,
      occupation: payload?.occupation,
      document_number: payload?.document_number,
      ...toShareholderPersonalMetaPayload(payload)
    };

    const { data } = await api.put(`/shareholders/${shareholderId}`, parsedPayload, config);

    return data;
  }
  public static async registerDocumentsShareholders(
    api: AxiosInstance,
    shareholderId: number,
    payload: registerDocumentsShareholders,
    config: ServiceBaseConfig
  ): Promise<any> {
    const { data } = await api.post(`/shareholders/${shareholderId}/documents`, payload, config);

    return data;
  }

  public static async advancingToTheNextStep(
    api: AxiosInstance,
    payload: null,
    config: ServiceBaseConfig
  ): Promise<any> {
    const { data } = await api.post('/plan/next-step', payload, config);
    if (data.success === false) {
      console.error(data.message);
    }
    return data;
  }

  public static async returningToPreviousStep(
    api: AxiosInstance,
    payload: null,
    config: ServiceBaseConfig
  ): Promise<any> {
    const { data } = await api.post('/plan/previous-step', payload, config);

    return data;
  }
}
